// Combobox buscable sobre una lista local de opciones (para picklists grandes,
// ej. ciudades/municipios). Filtra en memoria; no consulta al servidor.
import type { JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { matchesQuery } from "../lib/textMatch.ts";

export function LocalCombobox(
  { value, options, onChange, placeholder }:
  { value: string; options: readonly string[]; onChange: (v: string) => void; placeholder?: string },
): JSX.Element {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent): void => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const out: string[] = [];
    for (const o of options) {
      if (matchesQuery(o, q)) { out.push(o); if (out.length >= 50) break; }
    }
    return out;
  }, [q, options]);

  const pick = (o: string): void => { onChange(o); setQ(""); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      {value && !open ? (
        <div className="input flex items-center justify-between cursor-text" onClick={() => setOpen(true)}>
          <span className="truncate">{value}</span>
          <button type="button" className="text-ink-muted hover:text-ink-secondary ml-2 shrink-0" onClick={(e) => { e.stopPropagation(); onChange(""); setQ(""); }} aria-label="Limpiar">✕</button>
        </div>
      ) : (
        <div className="relative">
          <input
            className="input w-full pr-8"
            placeholder={placeholder ?? "Buscar…"}
            value={q}
            autoComplete="off"
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          />
          {q && (
            <button
              type="button"
              aria-label="Limpiar"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-secondary text-sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setQ("")}
            >✕</button>
          )}
        </div>
      )}
      {open && (
        <div className="absolute z-20 mt-1 w-full card max-h-64 overflow-y-auto shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-ink-muted">Sin resultados</div>
          ) : (
            filtered.map((o) => (
              <button type="button" key={o} className="w-full text-left px-3 py-2 text-sm hover:bg-surface-hover truncate" onClick={() => pick(o)}>{o}</button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
